import React, { useEffect, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import styled from 'styled-components';
import useMeasure from 'react-use-measure';
import { useForm } from 'react-hook-form';
import Cookies from 'js-cookie';
import beTwMerge from '@/utils/beTwMerge';
import { handleGaClick, sendGaEvent, trackEvent, trackEventWithObject } from '@/utils/gaDataPush';
import { getDataAttributes } from '@/utils/dataAttributes';
import { COUNTRIES, getCountry } from '@/utils/i18n';
import { IS_DEV } from '@/utils/env';
import useStore from '@/store/useStore';
import { FooterItemClickProps, FooterItemProp } from '@/store/types/IBuilder';
import { subscribeNewsletter, postKlaviyo, klaviyoIdentify } from '@/services/forms.service';
import { sha256 } from '@/utils/utils';
import { openDEChat } from '@/components/DigitalEngagementChat/DigitalEngagementChat';
import useMD5HashEmail from '@/hooks/useMD5HashEmail';
import { FadeIn } from './components/FadeIn';

const dataPush = (event: any, item: FooterItemProp) => {
  event.preventDefault();

  const { onClickParams } = item;

  (window as any).homePageDataPush(
    onClickParams.event,
    onClickParams.linkUrl || '',
    onClickParams.linkText || '',
    onClickParams.location
  );

  trackEvent(event);

  if (item.url) {
    window.location.href = item.url;
  }
};

const PanelContent = styled(animated.div)`
  overflow: hidden;
  ul {
    margin: 0;
    padding-top: 5px;
    padding-bottom: 5px;
  }
`;

const year = new Date().getFullYear();
const getCookieSelCountry = country => {
  if (country === undefined) {
    return true;
  }

  return country === 'US' || country === 'CA';
};

const setGeCookie = (code, currency, locale) => {
  Cookies.set('GlobalE_Data', `{"countryISO": "${code}", "currencyCode": "${currency}",  "cultureCode": "en-GB"}`, {
    path: '/',
    secure: true,
    domain: `.${window.location.hostname.replace('www.', '')}`,
  });
  localStorage.setItem('glCountry', '');
  Cookies.set('sel_country', code, { path: '/', secure: true });
  window.location.href = `/${locale}${locale ? '/' : ''}`;
};

function PanelItem({ footerItem, index, isCollapsed, onClick }) {
  const [ref, bounds] = useMeasure();
  const [trackOrderUrl, setTrackOrderUrl] = useState('/track-order/');
  const { localGeToggle } = useStore(state => state.glLocation);
  const [trackOrderAttr, setTrackOrderAttr] = useState(false);
  const panelContentAnimatedStyle = useSpring({
    height: !isCollapsed ? 0 : bounds.height,
  });
  const isGlobalE = localGeToggle !== null && !localGeToggle;

  useEffect(() => {
    if (isGlobalE)
      (window as any).GlobalECountryCode.then(code => {
        if (!['US', 'CA'].includes(code)) {
          setTrackOrderAttr(true);
          setTrackOrderUrl(
            IS_DEV ? 'https://www.bglobale.com/Order/Track/mZq0' : 'https://web.global-e.com/Order/Track/mZq0'
          );
        }
      });
  }, [isGlobalE]);

  return (
    <>
      <div className="column">
        <div className="h3">
          <a
            onClick={e => onClick(e, index)}
            data-toggle="collapse"
            data-parent="#accordion"
            href={`#collapse${index + 1}`}
            aria-controls={`collapse${index + 1}`}
            className={beTwMerge('swith', !isCollapsed && 'collapsed')}
            {...getDataAttributes(footerItem)}
            data-acsb-clickable="true"
            data-acsb-navigable="true"
            data-acsb-now-navigable="true"
            data-acsb-hover="true"
          >
            <span
              className="acsb-sr-only"
              data-acsb-sr-only="true"
              data-acsb-force-visible="true"
              aria-hidden="false"
              data-acsb-hidden="false"
            >
              {footerItem.titleInvisible}
            </span>
            <span className="symbol" aria-hidden="true" data-acsb-hidden="true" data-acsb-force-hidden="true" />
          </a>
          {footerItem.title.toLowerCase() === 'about' ? (
            <a
              data-link={footerItem?.url}
              className="link first-menu tw-font-[500]"
              onClick={e => onClick(e, index)}
              {...getDataAttributes(footerItem)}
              data-acsb-clickable="true"
              data-acsb-navigable="true"
              tabIndex={index}
              data-acsb-now-navigable="true"
              role="button"
              data-acsb-hover="true"
            >
              {footerItem?.title}
            </a>
          ) : (
            <a
              data-link={footerItem?.url}
              onClick={e => onClick(e, index)}
              className="link first-menu"
              data-acsb-clickable="true"
              data-acsb-navigable="true"
              tabIndex={0}
              data-acsb-now-navigable="true"
              role="button"
              data-acsb-hover="true"
            >
              {footerItem?.title}
            </a>
          )}
        </div>
      </div>
      <PanelContent style={panelContentAnimatedStyle}>
        <ul className="submenu" ref={ref}>
          {footerItem.items &&
            footerItem.items.map((item: FooterItemProp, footerItemIndex: number) => {
              if (Object.keys(item.onClickParams).length > 0) {
                return (
                  <li key={footerItemIndex} data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => dataPush(e, item)}
                      href={item?.url}
                      data-noninteract="true"
                      {...getDataAttributes(item)}
                      style={{ fontSize: '15px !important' }}
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      tabIndex={-1}
                      data-acsb-now-navigable="false"
                      data-acsb-hover="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                      data-acsb-tooltip="Use ←/→ to navigate"
                      aria-current="page"
                      data-acsb={item?.acsbTrigger || ''}
                    >
                      {item?.label}
                    </a>
                  </li>
                );
              }

              return (
                <li key={footerItemIndex} data-acsb-menu="li" data-acsb-menu-root="true">
                  {item?.url === '/track-order/' && trackOrderAttr ? (
                    <>
                      <a
                        onClick={trackEvent}
                        href="/track-order/"
                        {...getDataAttributes(item)}
                        data-noninteract="true"
                        data-acsb-clickable="true"
                        data-acsb-navigable="true"
                        tabIndex={-1}
                        data-acsb-now-navigable="false"
                        data-acsb-hover="true"
                        data-acsb-menu="a"
                        data-acsb-menu-root-link="true"
                        data-ge-hide-countries="all"
                        // eslint-disable-next-line react/no-unknown-property
                        trackOrderGE=""
                      >
                        {item?.label}
                      </a>
                      <a data-ge-show-countries="all" href={trackOrderUrl}>
                        Track Your Order
                      </a>
                    </>
                  ) : (
                    <a
                      onClick={trackEvent}
                      href={item?.url || '#'}
                      {...getDataAttributes(item)}
                      data-noninteract="true"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      tabIndex={-1}
                      data-acsb-now-navigable="false"
                      data-acsb-hover="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                      // eslint-disable-next-line react/no-unknown-property
                      trackOrderGE={null}
                    >
                      {item?.label}
                    </a>
                  )}
                </li>
              );
            })}
        </ul>
      </PanelContent>
    </>
  );
}

export default function MobileFooter({ links, className }: { links: any; className?: string }) {
  const footerLinks = links;
  const [activeIndex, setActiveIndex] = useState(null);
  const [isHiddenYourEmailAddress, setIsHiddenYourEmailAddress] = useState(true);
  const [checkPolicy, setcheckPolicy] = useState(false);
  const [phone, setPhone] = useState('');
  const [globalECountry, setGlobalECountry] = useState('');
  const { currCountry, isEuropeanUnionMember, currShowroom } = useStore(state => state.user);
  const isEurope = getCountry().code === 'GB' || isEuropeanUnionMember;
  const [isNwlVisible, setIsNwlVisible] = useState(false);
  const { convenientShowroomFullPhoneNumber } = useStore(state => state.user);
  const { localGeToggle } = useStore(state => state.glLocation);
  const countries = [...COUNTRIES];
  const { store, telCode } = useStore(state => state.showroom);
  const isGlobalE = localGeToggle !== null && !localGeToggle;
  const user = useStore(state => state.user);

  const setEmailUsModalVisible = useStore(state => state.setEmailUsModalVisible);

  const form = useForm();
  const {
    register,
    getValues,
    trigger,
    formState: { errors },
  } = form;

  const { hashEmail } = useMD5HashEmail();

  useEffect(() => {
    if (isGlobalE) {
      (window as any).getGlobalEPhoneNumber().then(p =>
        setPhone(() => {
          if (
            currCountry === 'united states' &&
            convenientShowroomFullPhoneNumber &&
            convenientShowroomFullPhoneNumber !== '1.800.691.0952'
          ) {
            return convenientShowroomFullPhoneNumber;
          }

          return p === '800.691.0952' ? '1.800.691.0952' : p;
        })
      );
      (window as any).GlobalECountryCode.then(setGlobalECountry);
    } else {
      setPhone(() => {
        if (
          currCountry === 'united states' &&
          convenientShowroomFullPhoneNumber &&
          convenientShowroomFullPhoneNumber !== '1.800.691.0952'
        ) {
          return `${convenientShowroomFullPhoneNumber}`;
        }

        return `${telCode}${store?.tel}`;
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalE, store]);

  const [callEventLabel, setCallEventLabel] = useState('Call_Other');

  useEffect(() => {
    if (currShowroom) {
      const capitalized = `Call_${currShowroom.charAt(0).toUpperCase()}${currShowroom.slice(1)}`;
      setCallEventLabel(capitalized);
    } else {
      setCallEventLabel('Call_Other');
    }
  }, [currShowroom]);

  useEffect(() => {
    const showTextMeUpdatesOffers =
      (user.ipCountry === 'US' || user.ipCountry === 'CA') && getCookieSelCountry(Cookies.get('sel_country'));
    setIsHiddenYourEmailAddress(showTextMeUpdatesOffers);
  }, [user.ipCountry]);

  async function validateEmail() {
    const isValidEmail = await trigger();
    const email = getValues('email');

    if (isValidEmail) {
      postKlaviyo('Footer Signup Form', email);
      klaviyoIdentify({ email });
      subscribeNewsletter(email)
        .then(async () => {
          const { location } = window as any;
          const hashedEmail = await hashEmail(email);
          (window as any).dataLayer.push({
            userEmail: email,
            userHashedEmail: await sha256(email),
            userPhoneNumber: '',
            userFirstName: '',
            userLastName: '',
            hashed_user_email_md5: hashedEmail,
          });
          (window as any).dataLayer.push({
            event: 'submit_email',
            location: 'footer',
          });
          (window as any).dataLayer.push({ event: 'Subscribe via Footer' });
          sendGaEvent({
            action: 'Footer',
            category: 'Newsletter Subscription',
            label: `${location.host}${location.pathname}${location.search}`,
          });
          setIsNwlVisible(true);
        })
        .catch(() => {
          // eslint-disable-next-line no-alert
          alert('Sent failed. Please try again.');
        });
    }
  }

  const openSocialMedia = (event: any, dataEvent: { source: string; url: string; label: string }) => {
    event.preventDefault();

    const { source, url, label } = dataEvent;
    (window as any).socialClickDataPush(source, url, label);
    (window as any).open(url, '_blank', 'noopener,noreferrer');
  };

  const pageDataPush = (event: any, clickParams: FooterItemClickProps) => {
    event.preventDefault();
    (window as any).homePageDataPush(
      clickParams.event,
      clickParams.linkUrl || '',
      clickParams.linkText || '',
      clickParams.location
    );

    trackEvent(event);

    if (clickParams.linkUrl) {
      window.location.href = clickParams.linkUrl;
    }
  };

  function onClickEmailUs(event, onClickParams) {
    pageDataPush(event, onClickParams);
    setEmailUsModalVisible(true);
  }

  const keyDownHandler = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      validateEmail();
    }
  };

  function onClick(event, index) {
    setActiveIndex(index !== activeIndex ? index : null);
    trackEvent(event);
  }

  const emailItem = {
    gaEvents: [
      {
        dataAttributes: [
          {
            dataAttributeValue: 'Mobile Footer',
            dataAttributeName: 'category',
          },
          {
            dataAttributeValue: 'Click',
            dataAttributeName: 'action',
          },
          {
            dataAttributeValue: 'Email',
            dataAttributeName: 'label',
          },
        ],
      },
    ],
    onClickParams: {
      linkText: 'Email Us',
      location: 'footer',
      event: 'internal_link_click',
    },
    dataLabel: 'Email Us',
    label: 'Email Us',
    url: '',
  };

  const showTextMeUpdatesOffers =
    (user.ipCountry === 'US' || user.ipCountry === 'CA') && getCookieSelCountry(Cookies.get('sel_country'));

  let redirectAttentiveLandingPage = '';
  if (user.ipCountry === 'US') {
    redirectAttentiveLandingPage = 'https://brilliantearth.attn.tv/p/OiJ/1000giftcard';
  } else if (user.ipCountry === 'CA') {
    redirectAttentiveLandingPage = 'https://brilliantearth-ca.attn.tv/p/YnY/landing-page';
  }

  return (
    <div className={className}>
      <div>
        <div className="footer-contact-us-mobile" role="navigation" aria-label="Page Menu">
          <div className="h4">contact</div>
          <ul className="list-inline" data-acsb-menu="ul">
            <li id="live-chat-link" data-acsb-menu="li" data-acsb-menu-root="true">
              <a
                onClick={e => {
                  e.preventDefault();
                  trackEventWithObject({
                    event: 'trackEvent',
                    eventCategory: 'Mobile Footer',
                    eventAction: 'Click',
                    eventLabel: 'Chat',
                    eventValue: '',
                    eventInteraction: true,
                  });
                  openDEChat();
                }}
                href="#"
                data-event1-action="Footer"
                data-event1-category="Live Chat"
                data-event1-label="Mobile"
                className="live_chat_button live_chat_footer_mobile "
                data-acsb-clickable="true"
                data-acsb-navigable="true"
                data-acsb-now-navigable="true"
                data-acsb-menu="a"
                role="button"
                data-acsb-menu-root-link="true"
                data-acsb-tooltip="Use ←/→ to navigate"
                data-acsb-hover="true"
              >
                <i
                  className="iconfont iconfont-chat"
                  aria-hidden="true"
                  data-acsb-hidden="true"
                  data-acsb-force-hidden="true"
                />
                chat
              </a>
            </li>
            <li data-acsb-menu="li" data-acsb-menu-root="true">
              <a
                id="footer-telephone-xs"
                className="phone_click"
                href={`tel:+${phone}`}
                onClick={e => {
                  trackEventWithObject({ event: 'mobile_click_to_call' });
                  trackEvent(e, true);
                }}
                data-event1-category="Mobile Footer"
                data-event1-action="Click"
                data-event1-label={callEventLabel}
                data-acsb-clickable="true"
                data-acsb-navigable="true"
                data-acsb-now-navigable="true"
                data-acsb-menu="a"
                data-acsb-menu-root-link="true"
                data-acsb-hover="true"
              >
                <i
                  className="iconfont iconfont-tel1"
                  aria-hidden="true"
                  data-acsb-hidden="true"
                  data-acsb-force-hidden="true"
                />
                Call
              </a>
            </li>
            {((isGlobalE !== null && !isGlobalE && currCountry === 'united states') || globalECountry === 'US') && (
              <li>
                <a
                  id="footer-sms"
                  href="sms: +1 415.520.6526"
                  data-category="Mobile Footer"
                  data-action="Click"
                  data-label="SMS"
                  data-noninteract="false"
                >
                  <i className="iconfont iconfont-sms" />
                  text
                </a>
              </li>
            )}
            <li data-acsb-menu="li" data-acsb-menu-root="true">
              <a
                className="mail_to_support"
                onClick={event =>
                  onClickEmailUs(event, {
                    linkText: 'Email Us',
                    location: 'footer',
                  })
                }
                href="#"
                {...getDataAttributes(emailItem)}
                id="recaptcha_footer"
                data-acsb-clickable="true"
                data-acsb-navigable="true"
                data-acsb-now-navigable="true"
                data-acsb-menu="a"
                role="button"
                data-acsb-menu-root-link="true"
                data-acsb-hover="true"
              >
                <i
                  className="iconfont iconfont-mail2"
                  aria-hidden="true"
                  data-acsb-hidden="true"
                  data-acsb-force-hidden="true"
                />
                email
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div
        className="wrapper ir217-footer-links footer-mobile-links"
        data-acsb-page-footer="true"
        role="contentinfo"
        aria-label="Footer"
      >
        <div className="tw-flex tw-flex-wrap" id="accordion" aria-multiselectable="true" role="tablist">
          {footerLinks &&
            footerLinks.map(({ footerItem }, footerLinkIndex: number) => (
              <div
                key={footerLinkIndex}
                role="tab"
                className={`drop_down tw-basis-full desktop:tw-w-3/12 large:tw-w-2/12 ${
                  footerLinkIndex === 0 ? 'fore1' : ''
                }`}
              >
                <PanelItem
                  footerItem={footerItem}
                  index={footerLinkIndex}
                  isCollapsed={footerLinkIndex === activeIndex}
                  onClick={onClick}
                />

                {footerLinkIndex + 1 === footerLinks?.length && (
                  <div className="tw-basis-full desktop:tw-w-4/12 large:tw-w-4/12 ">
                    <div className="column">
                      <div className="h3">
                        <span className="link tw-uppercase" style={{ paddingLeft: 24 }}>
                          Sign Up
                        </span>
                      </div>
                      <div className="tw-px-6 tw-mt-[10px]">
                        <div className="col-md-12">
                          <ul className="list-unstyled tw-mb-[15px]">
                            {showTextMeUpdatesOffers && (
                              <li className="tw-mb-[20px]">
                                <a href={redirectAttentiveLandingPage} target="_blank" rel="noreferrer">
                                  Text me updates and offers
                                </a>
                              </li>
                            )}

                            <li className="">
                              <a
                                href=""
                                onClick={event => {
                                  event.preventDefault();
                                  setIsHiddenYourEmailAddress(isHidden => !isHidden);
                                }}
                              >
                                Email me updates and offers
                              </a>
                            </li>
                          </ul>
                        </div>
                        <ul
                          className={beTwMerge(
                            'submenu submenu-adress-form !tw-mt-[23px]',
                            !isHiddenYourEmailAddress ? '' : 'hidden'
                          )}
                        >
                          <li className="hidden-xs" aria-hidden="true" data-acsb-hidden="true">
                            Send me Brilliant Earth news, updates and offers.
                          </li>
                          <li className="address-group-wrap be-join-movement !tw-pb-[0px] !tw-mb-[15px]">
                            <FadeIn props={{ opacity: isNwlVisible ? 1 : 0 }} visible={isNwlVisible}>
                              <div>Thank you for subscribing to the Brilliant Earth newsletter.</div>
                            </FadeIn>
                            {!isNwlVisible && (
                              <form
                                method="post"
                                className="footer-email tw-font-book"
                                data-acsb-form-uuid="aayn6y706hut"
                                data-acsb-form-type="standard"
                              >
                                <div
                                  className={beTwMerge(
                                    'tw-group tw-flex tw-h-11 tw-w-full tw-relative tw-z-0',
                                    errors.email?.message && 'is-error'
                                  )}
                                >
                                  <input
                                    type="text"
                                    {...register('email', {
                                      required: 'Email Address is required.',
                                      validate: {
                                        isValid: v =>
                                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v) ||
                                          'Invalid Email Address.',
                                      },
                                    })}
                                    onKeyDown={keyDownHandler}
                                    className="border-normal tw-peer tw-block tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error tw-font-AvenirBook"
                                    placeholder=" "
                                    id="footer_newsletter"
                                    maxLength={75}
                                    aria-label="Your Email Address"
                                    data-acsb-navigable="true"
                                    data-acsb-now-navigable="true"
                                    aria-hidden="false"
                                    data-acsb-hidden="false"
                                    data-acsb-validation-uuid="footer_newsletter"
                                    data-acsb-field-visible="true"
                                    data-acsb-tooltip="Your Email Address"
                                  />
                                  <label
                                    htmlFor="footer_newsletter"
                                    className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-3.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500 tw-font-AvenirBook"
                                  >
                                    Your Email Address
                                  </label>
                                  <button
                                    className="border-normal tw-size-11 tw-min-w-11 tw-appearance-none tw-rounded-none tw-bg-darkGreen tw-pt-1.5 tw-uppercase tw-text-white tw-outline-none hover:tw-bg-darkGreenHover"
                                    type="button"
                                    aria-label="Submit Email Address"
                                    data-acsb-clickable="true"
                                    data-acsb-navigable="true"
                                    data-acsb-now-navigable="true"
                                    onClick={validateEmail}
                                  >
                                    <svg
                                      fill="none"
                                      height="17"
                                      viewBox="0 0 10 17"
                                      width="10"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M1.42462 1L8.84924 8.42462L1.42462 15.8492"
                                        stroke="white"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </div>
                                {errors.email?.message && (
                                  <div className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-error">
                                    {errors?.email.message as string}
                                  </div>
                                )}

                                {isEurope && (
                                  <div className="tw-mb-2 tw-flex tw-items-start tw-mt-[20px]">
                                    <input
                                      {...register('policy', {
                                        required:
                                          'Agreement to our Privacy Policy and Terms and Conditions is required.',
                                      })}
                                      className={beTwMerge(
                                        'border-normal !tw-mt-0 tw-size-[18px] tw-rounded-none tw-border tw-border-[#d9d9d9] tw-text-darkGreen tw-ring-0 checked:tw-border-current focus:!tw-outline-0 focus:tw-ring-0',
                                        errors.policy?.message && 'tw-border-error'
                                      )}
                                      type="checkbox"
                                      data-tw="checkbox"
                                      id="footer_agree_policy_checkbox"
                                      checked={checkPolicy}
                                      onClick={() => setcheckPolicy(!checkPolicy)}
                                      tabIndex={0}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                          setcheckPolicy(!checkPolicy);
                                        }
                                      }}
                                    />
                                    <div className="tw-ms-2">
                                      <label
                                        className="tw-relative tw-top-0.5 tw-mb-0 tw-text-xs tw-font-normal"
                                        htmlFor="footer_agree_policy_checkbox"
                                      >
                                        By checking this box and submitting my email address, I agree to Brilliant
                                        Earth&apos;s{' '}
                                        <a
                                          href="/en-gb/security-and-privacy-policies/#privacy"
                                          style={{ padding: 0, display: 'inline' }}
                                          className="td-u2"
                                        >
                                          Privacy Policy
                                        </a>{' '}
                                        and{' '}
                                        <a
                                          href="/en-gb/terms-and-conditions/"
                                          className="td-u2"
                                          style={{ padding: 0, display: 'inline' }}
                                        >
                                          Terms and Conditions.
                                        </a>
                                      </label>
                                      {errors.policy?.message && (
                                        <div className="tw-mt-2 tw-mb-2 tw-text-[11px] tw-text-error">
                                          {errors?.policy.message as string}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <div className="join-email" aria-hidden="true" data-acsb-hidden="true" />
                                <button
                                  type="submit"
                                  className="acsb-sr-only"
                                  data-acsb-sr-only="true"
                                  aria-hidden="false"
                                  data-acsb-hidden="false"
                                  data-acsb-force-visible="true"
                                >
                                  Submit
                                </button>
                              </form>
                            )}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          <div className="ir249-footer-serves">
            <div className="wrapper tw-p-0">
              <nav aria-label="Footer Menu">
                <ul className="list-inline ir217-follow-us tw-px-6 tw-flex tw-justify-between" data-acsb-menu="ul">
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'youtube',
                          url: 'https://www.youtube.com/@BrilliantEarthInc',
                          label: 'YouTube',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.youtube.com/@BrilliantEarthInc"
                      className="iconfont iconfont-youtube hover:tw-no-underline"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="YouTube"
                      data-event2-category="external"
                      data-event2-action="click"
                      data-event2-label="www.youtube.com/watch?v=ERhI4ULhFlc"
                      data-acsb-tooltip="YouTube | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                      data-acsb-hover="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        YouTube YouTube New Window
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'instagram',
                          url: 'https://www.instagram.com/brilliantearth/',
                          label: 'Instagram',
                        });

                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.instagram.com/brilliantearth/"
                      className="iconfont iconfont-instagram hover:tw-no-underline"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Instagram"
                      data-event2-category="external"
                      data-event2-action="click"
                      data-event2-label="instagram.com/brilliantearth"
                      data-acsb-tooltip="Instagram | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                      data-acsb-hover="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Instagram Instagram New Window
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'facebook',
                          url: 'https://www.facebook.com/brilliantearth',
                          label: 'Facebook',
                        });

                        trackEvent(e, true);
                      }}
                      href="https://www.facebook.com/brilliantearth"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="iconfont iconfont-facebook hover:tw-no-underline active:tw-outline-none focus:tw-outline-none"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Facebook"
                      data-acsb-tooltip="Facebook | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="false"
                      data-acsb-menu="a"
                      role="button"
                      data-acsb-menu-root-link="true"
                      data-acsb-force-unnavigable="true"
                      tabIndex={-1}
                      aria-hidden="true"
                      data-acsb-hidden="true"
                      data-acsb-force-hidden="true"
                      data-acsb-hover="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Facebook
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'pinterest',
                          url: 'https://www.pinterest.com/brilliantearth/',
                          label: 'Pinterest',
                        });

                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.pinterest.com/brilliantearth/"
                      className="iconfont iconfont-pinterest hover:tw-no-underline"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Pinterest"
                      data-event2-category="external"
                      data-event2-action="click"
                      data-event2-label="pinterest.com/brilliantearth/"
                      data-acsb-tooltip="Pinterest | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                      data-acsb-hover="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Pinterest Pinterest New Window
                      </span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'twitter',
                          url: 'https://twitter.com/BrilliantEarth',
                          label: 'Twitter',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://twitter.com/BrilliantEarth"
                      className="iconfont iconfont-twitter hover:tw-no-underline"
                      title="Twitter"
                      data-category="Mobile Footer"
                      data-action="Click"
                      data-label="Twitter"
                    >
                      <span className="sr-only">Twitter</span>
                    </a>
                  </li>
                  <li data-acsb-menu="li" data-acsb-menu-root="true">
                    <a
                      onClick={e => {
                        openSocialMedia(e, {
                          source: 'linkedin',
                          url: 'https://www.linkedin.com/company/brilliantearth',
                          label: 'Linkedin',
                        });
                        trackEvent(e, true);
                      }}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href="https://www.linkedin.com/company/brilliantearth"
                      className="iconfont iconfont-linkedin hover:tw-no-underline"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Linkedin"
                      data-event2-category="external"
                      data-event2-action="click"
                      data-event2-label="www.linkedin.com/company/brilliantearth"
                      data-acsb-tooltip="Linkedin | New Window"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-menu="a"
                      data-acsb-menu-root-link="true"
                      data-acsb-hover="true"
                    >
                      <span
                        className="sr-only"
                        aria-hidden="false"
                        data-acsb-hidden="false"
                        data-acsb-sr-only="true"
                        data-acsb-force-visible="true"
                      >
                        Linkedin LinkedIn New Window
                      </span>
                    </a>
                  </li>
                </ul>
              </nav>
              <ul className="list-inline" id="ir217-copyright">
                <li>
                  <span>
                    <a
                      onClick={e =>
                        handleGaClick(
                          e,
                          'internal_link_click',
                          '/terms-and-conditions/',
                          'Terms &amp; Conditions',
                          'footer'
                        )
                      }
                      href="/terms-and-conditions/"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Terms & Conditions"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      Terms &amp; Conditions
                    </a>
                  </span>
                </li>
                <li>
                  <span>
                    <a
                      href="/security-and-privacy-policies/#privacy"
                      onClick={e =>
                        handleGaClick(
                          e,
                          'internal_link_click',
                          '/security-and-privacy-policies/#privacy',
                          'Privacy Policy',
                          'footer'
                        )
                      }
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Privacy Policy"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </li>
                <li>
                  <span>
                    <a
                      href="/security-and-privacy-policies/#interest_based_ads"
                      onClick={e =>
                        handleGaClick(
                          e,
                          'internal_link_click',
                          '/security-and-privacy-policies/#interest_based_ads',
                          'Interest-Based Ads',
                          'footer'
                        )
                      }
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Interest Based Ads"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      Interest-Based Ads
                    </a>
                  </span>
                </li>
                <li>
                  <span>
                    <a
                      onClick={e => {
                        trackEvent(e);
                        window['OneTrust']?.ToggleInfoDisplay();
                      }}
                      href="#"
                      role="button"
                      tabIndex={0}
                      className="focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Do Not Share My Personal Information"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      Do Not Sell or Share My Personal Information
                    </a>
                  </span>
                </li>
                <li>
                  <span>
                    <a
                      href="/ca-transparency-act/"
                      onClick={e =>
                        handleGaClick(
                          e,
                          'internal_link_click',
                          '/ca-transparency-act/',
                          'CA Transparency Act',
                          'footer'
                        )
                      }
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="CA Transparency Act"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      CA Transparency Act
                    </a>
                  </span>
                </li>
                <li>
                  <span>
                    <a
                      href="/security-and-privacy-policies/#CPRA"
                      onClick={e =>
                        handleGaClick(
                          e,
                          'internal_link_click',
                          '/security-and-privacy-policies/#CPRA',
                          'CPRA',
                          'footer'
                        )
                      }
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="CPRA"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      CPRA
                    </a>
                  </span>
                </li>
                <li>
                  <span>
                    <a
                      href="/sitemap/"
                      onClick={e => handleGaClick(e, 'internal_link_click', '/sitemap/', 'Site Map', 'footer')}
                      data-event1-category="Mobile Footer"
                      data-event1-action="Click"
                      data-event1-label="Site Map"
                      data-acsb-clickable="true"
                      data-acsb-navigable="true"
                      data-acsb-now-navigable="true"
                      data-acsb-hover="true"
                    >
                      Site Map
                    </a>
                  </span>
                </li>
                <li className="first">
                  <span>©{year} Brilliant Earth, LLC</span>
                </li>
              </ul>
              <ul className="list-inline tw-text-center tw-text-[10px]">
                {countries
                  .sort((a, b) => a.code.localeCompare(b.code))
                  .map(({ code, currency, locale }, i) => (
                    <li key={code + i}>
                      <a
                        data-category="Desktop Footer"
                        data-action="Click"
                        className="hover:!tw-no-underline focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                        data-label={`Subfolder | ${currency}`}
                        onClick={() => setGeCookie(code, currency, locale)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setGeCookie(code, currency, locale);
                          }
                        }}
                      >
                        {i > 0 && '|'}&nbsp;&nbsp; {currency}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
